import React, { ReactElement, useState, useCallback, useRef, RefObject, useEffect } from "react";

// widgets
import { useFocusTrap, getActiveElement, useFocusDialog } from "@swiggy-private/core-ui";

// interfaces
import { CartItem } from "@swiggy-private/cart-types";

// constants
import { TEXTS } from "@cart-ui/constants/index";
import { priceInRupee } from "@cart-ui/helpers/Price";
import { CUSTOMISATION_HALF_CARD_TEST_IDS } from "@swiggy-private/cart-test-ids";

//components
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import {
    CustomiseHeader,
    CustomiseHeaderContent,
    CustomiseHeaderContentName,
    CustomiseHeaderContentPrice,
    StyledFoodIcon,
    CustomiseActions,
} from "../styles";

export interface CustomiseRepeatModalProps {
    isVeg: CartItem["isVeg"];
    name: string;
    price: number;
    onCustomise: VoidFunction;
    onRepeat: VoidFunction;
    hideOnOverlayClick?: boolean;
    onClose?: VoidFunction;
}

type CustomiseRepeatModalContentProps = Omit<CustomiseRepeatModalProps, "hideOnOverlayClick" | "onClose">;

export const CustomiseRepeatModalContent = React.memo(function CustomiseRepeatModalContent(
    props: CustomiseRepeatModalContentProps,
): React.ReactElement {
    useFocusTrap();
    const headerDivRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);

    useEffect(() => {
        previouslyFocusedElement.current = getActiveElement();
    });

    useFocusDialog(headerDivRef, previouslyFocusedElement);

    return (
        <>
            <CustomiseHeader>
                <StyledFoodIcon vegType={props.isVeg} />
                <CustomiseHeaderContent>
                    <CustomiseHeaderContentName
                        variant="V2.heading2.neutral.bold"
                        color="text_High_Emphasis"
                        ref={headerDivRef as RefObject<HTMLDivElement>}
                        role="heading"
                        aria-level={3}
                        tabIndex={1}
                    >
                        {props.name}
                    </CustomiseHeaderContentName>
                    <CustomiseHeaderContentPrice variant="V2.body2.neutral.regular" color="text_Med_Emphasis">
                        {priceInRupee(props.price)}
                    </CustomiseHeaderContentPrice>
                </CustomiseHeaderContent>
            </CustomiseHeader>
            <CustomiseActions>
                <SuperButton
                    variant="fullwidth"
                    buttonType="secondary"
                    buttonSize="large"
                    onClick={props.onCustomise}
                    data-testid={CUSTOMISATION_HALF_CARD_TEST_IDS.I_WILL_CHOOSE}
                >
                    {TEXTS.I_LL_CHOOSE}
                </SuperButton>
                <SuperButton
                    variant="fullwidth"
                    buttonType="primary"
                    buttonSize="large"
                    onClick={props.onRepeat}
                    data-testid={CUSTOMISATION_HALF_CARD_TEST_IDS.REPEAT}
                >
                    {TEXTS.REPEAT_LAST}
                </SuperButton>
            </CustomiseActions>
        </>
    );
});

export function CustomiseRepeatModal(props: CustomiseRepeatModalProps): ReactElement {
    const { hideOnOverlayClick = true, onClose, ..._props } = props;
    const [actionOnClose] = useState<VoidFunction | undefined>();
    const _onClose = useCallback((): void => {
        actionOnClose && actionOnClose();
        onClose && onClose();
    }, [onClose, actionOnClose]);

    return (
        <HalfCard isOpen closeOnOverlayClick={hideOnOverlayClick} onClose={_onClose} hasCustomPadding>
            <CustomiseRepeatModalContent {..._props} />
        </HalfCard>
    );
}
